import React from 'react'
import PropTypes from 'prop-types'
import Raven from 'raven-js'
import ReactDOM from 'react-dom'
import Loadable from 'react-loadable'
import initReactFastclick from 'react-fastclick'
import { ConnectedRouter } from 'connected-react-router'
import { Provider } from 'react-redux'
import { Route, Switch, Redirect } from 'react-router-dom'
import { createBrowserHistory } from 'history'
import ReactPixel from 'react-facebook-pixel';


import configureStore from './redux/store'
import LoaderOverlay from './Components/LoaderOverlay'
import Layout from './Components/UI/Layout'
import AuthContainer from './Containers/AuthContainer'
import WebviewContainer from './Containers/WebviewContainer'

import './styles/index.css'

// Install Raven for tracking exceptions.
if (process.env.REACT_APP_SENTRY_DSN) {
  Raven.config(process.env.REACT_APP_SENTRY_DSN, {
    environment: (process.env.REACT_APP_ENVIRONMENT ? process.env.REACT_APP_ENVIRONMENT : 'production')
  }).install()
}

if (process.env.REACT_APP_FB_PIXEL_ID) {
  const advancedMatching = { };
  const options = {
      autoConfig: true,   // set pixel's autoConfig
      debug: false,     // enable logs
  };
  ReactPixel.init(process.env.REACT_APP_FB_PIXEL_ID, advancedMatching, options);
}



// Stick a singleton loader overlay into the loader-overlay-portal so that a full-screen
// loader can be turned on via LoaderOverlay.Toggle
ReactDOM.render(
  <LoaderOverlay />,
  document.getElementById('loader-overlay-portal')
)

/*
 * A loading placeholder component to show while the code-splitter is loading
 * the necessary chunks.
 */
const BootLoader = ({ pastDelay, error }) => {
  if (error) {
    return <div>Sorry, there was a problem loading the page.</div>
  } else if (pastDelay) {
    return <LoaderOverlay.Toggle active />
  } else {
    return null
  }
}

BootLoader.propTypes = {
  pastDelay: PropTypes.bool,
  error: PropTypes.object
}

/*
 * Apply code-splitting per-page and use the loader while the code is loaded. Perhaps,
 * we can optimize the bundle if we apply code-splitting per domain. Note that the
 * delay is set to 0ms because current the AuthContainer starts loading first and we want
 * a smooth transition to the Loadable loader.
 */
const asyncRoute = (loader) => {
  return Loadable({ loader: loader, loading: BootLoader, delay: 0 })
}

const InvestmentIntroduction = asyncRoute(
  () => import('./Pages/Onboarding/InvestmentIntroduction')
)

const ShawbrookOnboardingPage = asyncRoute(
  () => import('./Pages/Onboarding/ShawbrookOnboarding')
)

const FundsFlow = asyncRoute(
  () => import('./Pages/Onboarding/FundsFlow')
)

const Documents = asyncRoute(
  () => import('./Pages/Documents'),
)

const Billing = asyncRoute(
  () => import('./Pages/Billing'),
)

const Portfolio = asyncRoute(
  () => import('./Pages/Portfolio')
)

const InvestmentOverview = asyncRoute(
  () => import('./Pages/InvestmentOverview')
)

const Article = asyncRoute(
  () => import('./Pages/Article')
)

const RegularsFeedback = asyncRoute(
  () => import('./Pages/RegularsFeedback')
)

const RegularsFeedbackSuccess = asyncRoute(
  () => import('./Pages/RegularsFeedbackSuccess')
)

/* Splitter Pages */

const Splitter = asyncRoute(
  () => import('./Pages/Splitter/Splitter')
)

const SplitterIntroduction = asyncRoute(
  () => import('./Pages/Splitter/SplitterIntroduction')
)

const SplitterSuccess = asyncRoute(
  () => import('./Pages/Splitter/SplitterSuccess')
)

/* -------------- */

/* Initial Onboarding Pages */

const OnboardingRoute = asyncRoute(
  () => import('./Pages/InitialOnboarding/OnboardingRoute')
)

const CompletedOnboarding = asyncRoute(
  () => import('./Pages/InitialOnboarding/CompletedOnboarding')
)

const EmailAndTermsAgreement = asyncRoute(
  () => import('./Pages/InitialOnboarding/EmailAndTermsAgreement')
)

const PersonalDetails = asyncRoute(
  () => import('./Pages/InitialOnboarding/PersonalDetails')
)

const InstitutionLink = asyncRoute(
  () => import('./Pages/InitialOnboarding/InstitutionLink')
)

const FundingSource = asyncRoute(
  () => import('./Pages/FundingSource/FundingSource')
)

const FundingSourceSetup = asyncRoute(
  () => import('./Pages/FundingSource/FundingSourceSetupFromLink')
)


const FundingSourceSetupFromLink = asyncRoute(
  () => import('./Pages/InitialOnboarding/FundingSource')
)

const SwitchingComparison = asyncRoute(
  () => import('./Pages/Switching/Comparison')
)

const Roundups = asyncRoute(
  () => import('./Pages/InitialOnboarding/Roundups')
)

const SwitchingChangeAddress = asyncRoute(
  () => import('./Pages/Switching/ChangeAddress')
)

const SwitchingSupplier = asyncRoute(
  () => import('./Pages/Switching/Supplier')
)

const SwitchingVsPage = asyncRoute(
  () => import('./Pages/Switching/VsPage')
)

const RoundupBreakdown = asyncRoute(
  () => import('./Pages/Savings/Roundup')
)

const SwitchFlow = asyncRoute(
  () => import('./Pages/Switching/SwitchFlow')
)

const LostMoney = asyncRoute(
  () => import('./Pages/Recommendations/LostMoney')
)

const ValueScreen = asyncRoute(
  () => import('./Pages/Recommendations/ValueScreen')
)

const DepositFlow = asyncRoute(
  () => import('./Pages/Deposit')
)

const VerificationStandaloneCollector = asyncRoute(
  () => import('./Pages/VerificationStandaloneCollector')
)

/* Accounts Section */

const CreateAccount = asyncRoute(
  () => import('./Pages/Accounts/CreateAccount')
)

const AccountFeed = asyncRoute(
  () => import('./Pages/Accounts/Feed')
)

// Removes the touch delay on mobile.
initReactFastclick()

export const history = createBrowserHistory()
const store = configureStore({}, history)

const NotFound404 = () => (
  <Layout.Hero>
    <h1>Uh oh...</h1>
    <p>
      Doesn&rsquo;t look like we&rsquo;ve got that page. Were you given a broken link?
    </p>
  </Layout.Hero>
)

ReactDOM.render(
  <Provider store={ store }>
    <WebviewContainer>
      <AuthContainer>
        <ConnectedRouter history={ history }>
          <Switch>
            {/* Initial Onboarding */}
            <Route exact path="/onboarding" component={OnboardingRoute} />
            <Route exact path="/onboarding/terms" component={EmailAndTermsAgreement} />
            <Route exact path="/onboarding/personal-details" component={PersonalDetails} />
            <Route exact path="/onboarding/institution-link" component={InstitutionLink} />
            <Route exact path="/onboarding/funding-source" component={FundingSourceSetupFromLink} />
            <Route exact path="/onboarding/roundups" component={Roundups} />
            <Route exact path="/onboarding/completed" component={CompletedOnboarding} />

            {/* Funding Source */}
            <Route exact path="/funding-source" component={FundingSource} />
            <Route exact path="/funding-source-setup-from-link/:linkId" component={FundingSourceSetup} />

            {/* Investments Onboarding */}
            <Redirect strict exact from="/onboarding/investment" to="/onboarding/fund" />
            <Route exact path="/investment/introduction" component={InvestmentIntroduction} />
            <Route exact path="/onboarding/fund" component={FundsFlow} />
            <Route exact path="/onboarding/shawbrook" component={ShawbrookOnboardingPage} />

            {/* Investments */}
            <Redirect strict exact from='/portfolio' to='/portfolio/' />
            <Route exact path="/portfolio/" component={ Portfolio } />
            <Route path="/portfolio/investment" component={ InvestmentOverview } />

            {/* Recommendations */}
            <Route exact path="/lost-money" component={ LostMoney } />
            <Route exact path="/value-screen" component={ ValueScreen } />

            {/* Accounts */}
            <Route exact path="/accounts/create" component={ CreateAccount } />
            <Route exact path="/accounts/:id" component={ AccountFeed } />

            {/* Splitter */}
            <Route exact path="/splitter" component={ Splitter } />
            <Route path="/splitter/introduction" component={ SplitterIntroduction } />
            <Route path="/splitter/success" component={ SplitterSuccess } />

            {/* Savings */}
            <Route exact path="/savings/roundups/:id" component={ RoundupBreakdown } />

            {/* Deposit Flow */}
            <Route exact path="/deposit" component={ DepositFlow } />

            {/* Switching */}
            <Route exact path="/switching-not-supported" component={ NotFound404 } />
            <Route exact path="/switching/:type" component={ SwitchingComparison } />
            <Route exact path="/switching/:type/change-address" component={ SwitchingChangeAddress } />
            <Route exact path="/switching/:type/supplier/:supplierId" component={ SwitchingSupplier } />
            <Route exact path="/switching/:type/supplier/:supplierId/vs" component={ SwitchingVsPage } />
            <Route exact path="/switching/:type/supplier/:supplierId/switch" component={ SwitchFlow } />

            {/* Documents */}
            <Route path="/documents" component={ Documents } />

            {/* Billing */}
            <Route path="/billing" component={Billing} />

            {/* Articles */}
            <Route path="/articles" component={ Article } />

            {/* Regulars */}
            <Route path="/regulars/feedback/success" component={RegularsFeedbackSuccess} />
            <Route path="/regulars/feedback" component={RegularsFeedback} />

            {/* Verification */}
            <Route exact path="/verification-collector/:copyIdentifier/:verificationId" component={ VerificationStandaloneCollector } />

            {/* 404 */}
            <Route component={ NotFound404 } />
          </Switch>
        </ConnectedRouter>
      </AuthContainer>
    </WebviewContainer>
  </Provider>,
  document.getElementById('root')
);



// WEBPACK FOOTER //
// ./src/index.js